import { Link } from '@tanstack/react-router';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

type Tier = {
  name: string;
  price: string;
  cadence?: string;
  blurb: string;
  cta: { label: string; to: string };
  highlight?: boolean;
  features: string[];
};

const TIERS: Tier[] = [
  {
    name: 'Free audit',
    price: '$0',
    blurb: 'Run a one-shot audit on a public OpenAPI spec or docs URL. No credit card.',
    cta: { label: 'Run a free audit', to: '/signup' },
    features: [
      '1 audit per spec',
      '10-dimension agent-readiness score',
      'Top 5 prioritized fixes',
      'Sample llms.txt preview',
      'Public report link',
    ],
  },
  {
    name: 'Team',
    price: '$499',
    cadence: '/month',
    blurb: 'For API teams shipping weekly. PR-comment bot, hosted assets, drift watch.',
    cta: { label: 'Start free trial', to: '/signup' },
    highlight: true,
    features: [
      'Up to 3 specs / docs domains',
      'PR-comment bot on every spec or docs PR',
      'Hosted llms.txt + MCP server + Cursor rules',
      'Weekly drift checks against shipped SDKs',
      'Slack + email alerts',
      'Up to 10 seats',
    ],
  },
  {
    name: 'Business',
    price: '$1,499',
    cadence: '/month',
    blurb: 'For multi-product API surfaces and dedicated DevRel teams.',
    cta: { label: 'Start free trial', to: '/signup' },
    features: [
      'Up to 10 specs / docs domains',
      'Per-repo SDK consistency checks',
      'Custom audit rules and severity overrides',
      'SAML SSO + audit log',
      'Priority support, 1 business-day SLA',
      'Unlimited seats',
    ],
  },
  {
    name: 'Enterprise',
    price: 'Custom',
    blurb: 'Self-hosted runner, security review, dedicated solutions engineer.',
    cta: { label: 'Contact sales', to: '/signup' },
    features: [
      'Unlimited specs and SDK repos',
      'Self-hosted audit runner option',
      'Custom integrations (Jira, Linear, ServiceNow)',
      'Dedicated solutions engineer',
      'Security review + DPA + signed BAA on request',
    ],
  },
];

export function PricingPage() {
  return (
    <div className="mx-auto max-w-6xl px-6 py-20">
      <section className="max-w-3xl">
        <h1 className="text-4xl font-semibold tracking-tight md:text-5xl">
          Simple, usage-light pricing.
        </h1>
        <p className="mt-4 text-lg text-slate-600 dark:text-slate-400">
          Athena scales with how many APIs you ship, not how many requests they serve. Start with
          a free audit, upgrade when you want PR-time enforcement and hosted assets.
        </p>
      </section>

      <section className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
        {TIERS.map((tier) => (
          <TierCard key={tier.name} tier={tier} />
        ))}
      </section>

      <section className="mt-20">
        <h2 className="text-2xl font-semibold tracking-tight">Frequently asked</h2>
        <div className="mt-6 grid gap-6 md:grid-cols-2">
          <Faq
            q="What counts as a spec?"
            a="A single OpenAPI/Swagger document or one docs domain (e.g. docs.acme.com). If you publish multiple products under one umbrella, each surface is a separate spec."
          />
          <Faq
            q="Does the PR bot post on every PR?"
            a="No. It only comments on PRs that touch your spec, SDK, or docs paths — and stays silent on bot PRs, drafts, and unrelated changes. You configure the path globs in settings."
          />
          <Faq
            q="Can I cancel anytime?"
            a="Yes. Subscriptions are month-to-month with no minimum commitment. Annual billing gets two months free."
          />
          <Faq
            q="Do you store our source code?"
            a="No. Athena reads spec files and docs at audit time and discards them after the report renders. Generated assets (llms.txt, MCP server) are stored so we can serve them."
          />
        </div>
      </section>

      <section className="mt-20 rounded-xl border border-slate-200 bg-slate-50 p-8 text-center dark:border-slate-800 dark:bg-slate-950">
        <h2 className="text-2xl font-semibold tracking-tight">Not sure which tier you need?</h2>
        <p className="mx-auto mt-2 max-w-xl text-slate-600 dark:text-slate-400">
          Run the free audit first. The report includes a recommendation based on the size and
          shape of your API surface.
        </p>
        <div className="mt-6 flex justify-center gap-3">
          <Button size="lg" asChild>
            <Link to="/signup">Run a free audit</Link>
          </Button>
          <Button size="lg" variant="outline" asChild>
            <Link to="/sample-report">See a sample report</Link>
          </Button>
        </div>
      </section>
    </div>
  );
}

function TierCard({ tier }: { tier: Tier }) {
  return (
    <div
      className={cn(
        'flex flex-col rounded-xl border bg-white p-6 dark:bg-slate-900',
        tier.highlight
          ? 'border-[var(--color-accent)] shadow-[0_0_0_1px_var(--color-accent)]'
          : 'border-slate-200 dark:border-slate-800',
      )}
    >
      <div className="flex items-baseline justify-between">
        <h3 className="text-lg font-semibold">{tier.name}</h3>
        {tier.highlight && (
          <span className="rounded-full bg-[var(--color-accent)] px-2 py-0.5 text-xs font-medium text-white">
            Most popular
          </span>
        )}
      </div>
      <div className="mt-4 flex items-baseline gap-1">
        <span className="text-3xl font-semibold tracking-tight">{tier.price}</span>
        {tier.cadence && (
          <span className="text-sm text-slate-500 dark:text-slate-400">{tier.cadence}</span>
        )}
      </div>
      <p className="mt-3 text-sm text-slate-600 dark:text-slate-400">{tier.blurb}</p>

      <ul className="mt-6 flex-1 space-y-2.5">
        {tier.features.map((f) => (
          <li key={f} className="flex gap-2 text-sm">
            <CheckIcon />
            <span className="text-slate-700 dark:text-slate-300">{f}</span>
          </li>
        ))}
      </ul>

      <div className="mt-6">
        <Button
          variant={tier.highlight ? 'default' : 'outline'}
          className="w-full"
          asChild
        >
          <Link to={tier.cta.to}>{tier.cta.label}</Link>
        </Button>
      </div>
    </div>
  );
}

function CheckIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 20 20"
      className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--color-accent)]"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M16.704 5.29a1 1 0 0 1 .006 1.414l-7.07 7.146a1 1 0 0 1-1.42.005L3.29 8.95a1 1 0 1 1 1.42-1.408l4.225 4.262 6.355-6.42a1 1 0 0 1 1.414-.094z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function Faq({ q, a }: { q: string; a: string }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
      <div className="font-medium text-slate-900 dark:text-slate-100">{q}</div>
      <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">{a}</p>
    </div>
  );
}
